import { useState, useEffect, useRef, useCallback } from 'react';
import { WebContainer, type FileSystemTree } from '@webcontainer/api';
import { api } from '../api';

export type BootPhase =
  | 'idle'
  | 'fetching'
  | 'booting'
  | 'mounting'
  | 'installing'
  | 'starting'
  | 'ready'
  | 'error';

interface UseWebContainerOptions {
  influencerDbId: number;
  onServerReady?: (url: string) => void;
  onTerminalData?: (data: string) => void;
}

interface UseWebContainerReturn {
  phase: BootPhase;
  error: string | null;
  serverUrl: string | null;
  writeFile: (path: string, content: string) => Promise<void>;
  restart: () => void;
}

async function fetchAllFiles(
  influencerDbId: number,
  dirPath: string = '',
  onProgress?: (msg: string) => void,
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  const res = await api.listFiles(influencerDbId, dirPath);

  const dirs: string[] = [];
  const filePaths: string[] = [];

  for (const entry of res.entries) {
    if (entry.type === 'dir') {
      if (entry.path === 'node_modules' || entry.path.endsWith('/node_modules')) continue;
      if (entry.path === '.git' || entry.path.endsWith('/.git')) continue;
      dirs.push(entry.path);
    } else {
      filePaths.push(entry.path);
    }
  }

  const BATCH_SIZE = 8;
  for (let i = 0; i < filePaths.length; i += BATCH_SIZE) {
    const batch = filePaths.slice(i, i + BATCH_SIZE);
    const contents = await Promise.all(
      batch.map(async (p) => {
        try {
          const f = await api.getFile(influencerDbId, p);
          return { path: p, content: f.content };
        } catch {
          return { path: p, content: '' };
        }
      })
    );
    for (const c of contents) {
      result.set(c.path, c.content);
    }
    onProgress?.(`Fetched ${Math.min(i + BATCH_SIZE, filePaths.length)}/${filePaths.length} files...`);
  }

  for (const dir of dirs) {
    const sub = await fetchAllFiles(influencerDbId, dir, onProgress);
    for (const [k, v] of sub) {
      result.set(k, v);
    }
  }

  return result;
}

function buildFileTree(files: Map<string, string>): FileSystemTree {
  const tree: FileSystemTree = {};

  for (const [filePath, content] of files) {
    const parts = filePath.split('/');
    let current: any = tree;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (i === parts.length - 1) {
        current[part] = { file: { contents: content } };
      } else {
        if (!current[part]) {
          current[part] = { directory: {} };
        }
        current = current[part].directory;
      }
    }
  }

  return tree;
}

// ─── WebContainer patch files ────────────────────────────────────────────────
//
// react-router dev requires the native `workerd` binary (unavailable in
// WebContainers) AND its SSR module runner can't handle CJS react-dom/server
// (which uses `require` — unavailable in Vite 6's ESM evaluator context).
//
// Solution: bypass react-router dev entirely. Use plain `vite` +
// `@vitejs/plugin-react` with a browser-safe custom entry and mock loaders.
// None of these patches touch the actual GitHub repo files.

const PATCHED_VITE_CONFIG = `\
import { defineConfig } from "vite";
import react from "@vitejs/plugin-react";

export default defineConfig({
  plugins: [react()],
  resolve: { alias: { "~": "/src" } },
  server: { host: true },
});
`;

/**
 * Browser-safe root layout for WebContainer preview.
 *
 * Intentionally omits Links/Meta/Scripts/ScrollRestoration — those are
 * framework-mode-only components that require HydratedRouter's internal
 * context. With createMemoryRouter + RouterProvider they throw:
 * "You must render this element inside a <HydratedRouter>".
 * Global CSS is imported directly so Vite handles it instead.
 */
const WC_ROOT_TSX = `\
import { Outlet, useLoaderData } from "react-router";
import { Header } from "~/components/Header";
import { Footer } from "~/components/Footer";
import "~/styles/global.css";

interface WcRootLoaderData {
  brandName: string;
  cartQuantity: number;
  contactEmail?: string;
}

export default function WcRoot() {
  const data = (useLoaderData() ?? {}) as WcRootLoaderData;
  const brandName = data.brandName ?? "Preview Store";
  const cartQuantity = data.cartQuantity ?? 0;
  const contactEmail = data.contactEmail;
  return (
    <div className="app-container">
      <Header brandName={brandName} cartQuantity={cartQuantity} />
      <main className="main-content">
        <Outlet />
      </main>
      <Footer brandName={brandName} contactEmail={contactEmail} />
    </div>
  );
}
`;

/**
 * Custom SPA entry — createMemoryRouter with mock loaders so the app renders
 * without Cloudflare / Shopify context. Includes all storefront routes with
 * graceful empty-state fallbacks so nav links work in preview.
 */
const WC_MAIN_TSX = `\
import React from "react";
import { createRoot } from "react-dom/client";
import { createMemoryRouter, RouterProvider, useRouteError, Link } from "react-router";
import WcRoot from "./wc-root";
import Home from "./routes/home";
import About from "./routes/about";

const rootLoader = async () => ({
  brandName: "Preview Store",
  cartQuantity: 0,
  contactEmail: "",
});

const homeLoader = async () => ({
  featuredCollection: null,
  featuredProducts: [],
  brandName: "Preview Store",
});

const aboutLoader = async () => ({
  brandName: "Preview Store",
});

function PreviewPlaceholder({ title }: { title: string }) {
  return (
    <div style={{ padding: "5rem 2rem", textAlign: "center", color: "var(--color-text-primary, #fff)" }}>
      <h2 style={{ fontFamily: "var(--font-family-display, sans-serif)", fontSize: "2rem", marginBottom: "1rem" }}>{title}</h2>
      <p style={{ color: "var(--color-text-muted, rgba(255,255,255,0.5))", marginBottom: "2rem", fontSize: "0.9rem" }}>
        Live preview — Shopify data not available in editor
      </p>
      <Link to="/" style={{ color: "var(--color-primary, #ffd400)", textDecoration: "underline", fontSize: "0.9rem" }}>
        Back to Home
      </Link>
    </div>
  );
}

function RouteErrorBoundary() {
  const error = useRouteError() as Error;
  return (
    <div style={{ padding: "5rem 2rem", textAlign: "center", color: "var(--color-text-primary, #fff)" }}>
      <h2 style={{ fontFamily: "var(--font-family-display, sans-serif)", fontSize: "1.75rem", marginBottom: "1rem" }}>Preview Error</h2>
      <p style={{ color: "var(--color-text-muted, rgba(255,255,255,0.5))", marginBottom: "1rem", fontSize: "0.85rem", maxWidth: "480px", margin: "0 auto 1rem" }}>
        {error?.message ?? "This page requires live Shopify data to render fully."}
      </p>
      <Link to="/" style={{ color: "var(--color-primary, #ffd400)", textDecoration: "underline", fontSize: "0.9rem" }}>
        Back to Home
      </Link>
    </div>
  );
}

const router = createMemoryRouter([
  {
    path: "/",
    Component: WcRoot,
    loader: rootLoader,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, Component: Home, loader: homeLoader, errorElement: <RouteErrorBoundary /> },
      { path: "about", Component: About, loader: aboutLoader, errorElement: <RouteErrorBoundary /> },
      { path: "collections/:handle", element: <PreviewPlaceholder title="Collection" /> },
      { path: "products/:handle", element: <PreviewPlaceholder title="Product" /> },
      { path: "cart", element: <PreviewPlaceholder title="Cart" /> },
      { path: "search", element: <PreviewPlaceholder title="Search" /> },
      { path: "policies/:slug", element: <PreviewPlaceholder title="Policy" /> },
      {
        path: "*",
        element: (
          <div style={{ padding: "5rem 2rem", textAlign: "center", color: "var(--color-text-primary, #fff)" }}>
            <h2 style={{ fontFamily: "var(--font-family-display, sans-serif)", fontSize: "2rem", marginBottom: "1rem" }}>Page Not Found</h2>
            <p style={{ color: "var(--color-text-muted, rgba(255,255,255,0.5))", marginBottom: "2rem", fontSize: "0.9rem" }}>
              This route doesn&apos;t exist in the preview.
            </p>
            <Link to="/" style={{ color: "var(--color-primary, #ffd400)", textDecoration: "underline", fontSize: "0.9rem" }}>
              Back to Home
            </Link>
          </div>
        ),
      },
    ],
  },
]);

createRoot(document.getElementById("root")!).render(
  <RouterProvider router={router} />
);
`;

const WC_INDEX_HTML = `\
<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Preview</title>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/wc-main.tsx"></script>
  </body>
</html>
`;

/**
 * Move any CSS @import statements to the top of a file.
 * PostCSS requires @import to precede all other statements.
 */
function fixCssImportOrder(css: string): string {
  const lines = css.split('\n');
  const imports: string[] = [];
  const rest: string[] = [];
  for (const line of lines) {
    if (line.trim().startsWith('@import ')) {
      imports.push(line);
    } else {
      rest.push(line);
    }
  }
  if (imports.length === 0) return css;
  return [...imports, '', ...rest].join('\n');
}

export function useWebContainer({
  influencerDbId,
  onServerReady,
  onTerminalData,
}: UseWebContainerOptions): UseWebContainerReturn {
  const [phase, setPhase] = useState<BootPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [serverUrl, setServerUrl] = useState<string | null>(null);

  const wcRef = useRef<WebContainer | null>(null);
  const bootedRef = useRef(false);
  const serverReadyFiredRef = useRef(false);
  const onTerminalDataRef = useRef(onTerminalData);
  const onServerReadyRef = useRef(onServerReady);

  useEffect(() => { onTerminalDataRef.current = onTerminalData; }, [onTerminalData]);
  useEffect(() => { onServerReadyRef.current = onServerReady; }, [onServerReady]);

  const boot = useCallback(async () => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    serverReadyFiredRef.current = false;

    try {
      setPhase('fetching');
      setError(null);
      onTerminalDataRef.current?.('📦 Fetching project files from GitHub...\r\n');

      const files = await fetchAllFiles(influencerDbId, '', (msg) => {
        onTerminalDataRef.current?.(`  ${msg}\r\n`);
      });

      onTerminalDataRef.current?.(`✅ Fetched ${files.size} files\r\n\r\n`);

      setPhase('booting');
      onTerminalDataRef.current?.('🚀 Booting WebContainer...\r\n');
      const wc = await WebContainer.boot();
      wcRef.current = wc;

      setPhase('mounting');
      onTerminalDataRef.current?.('📁 Mounting file system...\r\n');
      const tree = buildFileTree(files);
      await wc.mount(tree);

      // Patch the project to run as a plain Vite SPA (no react-router dev / workerd / SSR).
      // react-dom/server is CJS-only in React 18 and uses `require()` which
      // fails in Vite 6's ESM module runner. Bypassing react-router dev entirely
      // avoids this whole class of issues.
      onTerminalDataRef.current?.('🔧 Patching config for browser-only preview...\r\n');

      // 1. Swap vite.config.ts to use @vitejs/plugin-react only
      await wc.fs.writeFile('vite.config.ts', PATCHED_VITE_CONFIG);

      // 2. Patch package.json: change dev script + add @vitejs/plugin-react devDep
      try {
        const pkgRaw = await wc.fs.readFile('package.json', 'utf-8');
        if (typeof pkgRaw === 'string') {
          const pkg = JSON.parse(pkgRaw);
          if (pkg.scripts?.dev) pkg.scripts.dev = 'vite';
          pkg.devDependencies = pkg.devDependencies ?? {};
          pkg.devDependencies['@vitejs/plugin-react'] = '^4.0.0';
          await wc.fs.writeFile('package.json', JSON.stringify(pkg, null, 2));
        }
      } catch { /* ignore */ }

      // 3. Write browser-safe root layout (no Cloudflare imports)
      await wc.fs.writeFile('src/wc-root.tsx', WC_ROOT_TSX);

      // 4. Write custom SPA entry with createMemoryRouter + mock loaders
      await wc.fs.writeFile('src/wc-main.tsx', WC_MAIN_TSX);

      // 5. Write index.html pointing to the custom entry
      await wc.fs.writeFile('index.html', WC_INDEX_HTML);

      // 6. Write About page placeholder if repo pre-dates it (older generated sites)
      try {
        await wc.fs.readFile('src/routes/about.tsx', 'utf-8');
      } catch {
        // File not in repo — write a minimal placeholder so wc-main.tsx can import it
        await wc.fs.writeFile('src/routes/about.tsx', `\
import { Link } from "react-router";
import "~/styles/global.css";
export default function About() {
  return (
    <div style={{ padding: "5rem 2rem", textAlign: "center", color: "var(--color-text-primary, #fff)" }}>
      <h1 style={{ fontFamily: "var(--font-family-display, sans-serif)", fontSize: "2.5rem", marginBottom: "1rem" }}>About Us</h1>
      <p style={{ color: "var(--color-text-muted, rgba(255,255,255,0.5))", marginBottom: "2rem" }}>
        This About page was added in a newer template version.<br />
        Regenerate the site draft to get a fully styled version.
      </p>
      <Link to="/" style={{ color: "var(--color-primary, #ffd400)", textDecoration: "underline" }}>Back to Home</Link>
    </div>
  );
}
`);
        onTerminalDataRef.current?.('🔧 About page not found in repo — wrote placeholder\r\n');
      }

      // 7. Fix CSS @import ordering in global.css (PostCSS requires imports first)
      try {
        const globalCss = await wc.fs.readFile('src/styles/global.css', 'utf-8');
        if (typeof globalCss === 'string' && globalCss.includes('@import')) {
          const fixed = fixCssImportOrder(globalCss);
          if (fixed !== globalCss) {
            await wc.fs.writeFile('src/styles/global.css', fixed);
            onTerminalDataRef.current?.('🔧 Fixed CSS @import order in global.css\r\n');
          }
        }
      } catch { /* ignore */ }

      onTerminalDataRef.current?.('✅ File system mounted & patched\r\n\r\n');

      setPhase('installing');
      onTerminalDataRef.current?.('📥 Installing dependencies (npm install)...\r\n');
      const installProcess = await wc.spawn('npm', ['install']);

      installProcess.output.pipeTo(
        new WritableStream({
          write(data) {
            onTerminalDataRef.current?.(data);
          },
        })
      );

      const installExitCode = await installProcess.exit;
      if (installExitCode !== 0) {
        throw new Error(`npm install failed with exit code ${installExitCode}`);
      }
      onTerminalDataRef.current?.('\r\n✅ Dependencies installed\r\n\r\n');

      setPhase('starting');
      onTerminalDataRef.current?.('▶️  Starting dev server (npm run dev)...\r\n');
      const devProcess = await wc.spawn('npm', ['run', 'dev']);

      devProcess.output.pipeTo(
        new WritableStream({
          write(data) {
            onTerminalDataRef.current?.(data);
          },
        })
      );

      wc.on('server-ready', (_port, url) => {
        if (serverReadyFiredRef.current) return;
        serverReadyFiredRef.current = true;
        setServerUrl(url);
        setPhase('ready');
        onTerminalDataRef.current?.(`\r\n🌐 Dev server ready at ${url}\r\n`);
        onServerReadyRef.current?.(url);
      });

      wc.on('error', (err) => {
        onTerminalDataRef.current?.(`\r\n❌ WebContainer error: ${err.message}\r\n`);
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setPhase('error');
      setError(msg);
      onTerminalDataRef.current?.(`\r\n❌ Error: ${msg}\r\n`);
    }
  }, [influencerDbId]);

  useEffect(() => {
    boot();
    return () => {
      wcRef.current?.teardown();
      wcRef.current = null;
      bootedRef.current = false;
    };
  }, [boot]);

  const writeFile = useCallback(async (path: string, content: string) => {
    if (!wcRef.current) return;
    await wcRef.current.fs.writeFile(path, content);
  }, []);

  const restart = useCallback(() => {
    wcRef.current?.teardown();
    wcRef.current = null;
    bootedRef.current = false;
    setPhase('idle');
    setServerUrl(null);
    setError(null);
    boot();
  }, [boot]);

  return { phase, error, serverUrl, writeFile, restart };
}
